import { useState } from 'react';
import type { ComponentType } from 'react';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';
import {
  LayoutGrid, FileStack, BookMarked, Users, Gauge, Settings, BookOpen, Building2,
  PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ThemeToggle } from './ThemeToggle';
import { RAIL_EASE, labelReveal } from './railMotion';
import { useMe } from '@/lib/useMe';
import { signOut } from '@/lib/authClient';
import { notifyError } from '@/lib/useMutationToast';

const EXPANDED_KEY = 'lyra:nav-expanded';

const NAV = [
  { to: '/', label: 'Panou', Icon: LayoutGrid, end: true },
  { to: '/mapari', label: 'Mapări', Icon: FileStack, end: false },
  { to: '/nomenclator', label: 'Nomenclator', Icon: BookMarked, end: false },
  { to: '/echipa', label: 'Echipă', Icon: Users, end: false },
  { to: '/utilizare', label: 'Utilizare', Icon: Gauge, end: false },
];

const OPERATOR_NAV = { to: '/operator/organizatii', label: 'Operator', Icon: Building2, end: false };

/** One nav entry. Collapsed = a centered icon with a hover tooltip; expanded =
 *  icon + label row (desktop only — the mobile top-bar stays icon-only, so the
 *  label is `md:inline` and the tooltip is dropped only when the label shows). */
function RailLink({
  to, label, Icon, end, expanded,
}: {
  to: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  end?: boolean;
  expanded: boolean;
}) {
  // Active state is computed here (not via NavLink's function className):
  // this RailLink is wrapped in Radix `TooltipTrigger asChild` when collapsed,
  // and Slot coerces a *function* className to its source string instead of
  // invoking it — which would apply a garbage jumble of both ternary branches
  // and knock the icon off-centre. A plain string className stays intact
  // through the Slot merge.
  const isActive = !!useMatch({ path: to, end: !!end });
  // The icon lives in a fixed 40px box that stays put across the whole
  // expand/collapse: centred in the collapsed rail, left-gutter when expanded
  // (item `md:pl-1` lands it at the same x). So nothing jumps — only the label
  // wipes open beside it. `md:w-full` on expand widens only the hover target.
  const link = (
    <NavLink
      to={to}
      end={end}
      aria-label={label}
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md motion-safe:transition-colors ${
        expanded ? 'md:w-full md:justify-start md:pl-1' : ''
      } ${isActive ? 'bg-teal-wash text-teal' : 'text-ink-2 hover:bg-wash'}`}
    >
      <span className="grid size-10 shrink-0 place-items-center"><Icon className="size-5" /></span>
      <span className={`text-[14px] font-medium ${labelReveal(expanded)}`}>{label}</span>
    </NavLink>
  );
  if (expanded) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

export function IconRail() {
  const { data: me } = useMe();
  const [expanded, setExpanded] = useState(() => localStorage.getItem(EXPANDED_KEY) === '1');
  function toggle() {
    setExpanded((v) => {
      const next = !v;
      localStorage.setItem(EXPANDED_KEY, next ? '1' : '0');
      return next;
    });
  }

  // platform_admin-only entry (Plan 3f Task 3) — appended rather than baked
  // into the static NAV array so ordinary org users never see it.
  const navigate = useNavigate();
  const nav = me?.globalRole === 'platform_admin' ? [...NAV, OPERATOR_NAV] : NAV;

  return (
    <nav
      className={`flex w-full shrink-0 flex-row items-center gap-1 overflow-x-auto border-b border-rule bg-wash px-2 py-2 motion-safe:transition-[width] motion-safe:duration-300 ${RAIL_EASE} md:flex-col md:overflow-visible md:border-b-0 md:border-r md:px-2 md:py-4 ${
        expanded ? 'md:w-56' : 'md:w-16'
      }`}
    >
      <div className={`flex h-10 w-10 items-center justify-center md:mb-3 ${expanded ? 'md:w-full md:justify-start md:pl-1' : ''}`}>
        <span className="grid size-10 shrink-0 place-items-center">
          <span className="grid size-9 place-items-center rounded-md bg-white shadow-sm ring-1 ring-black/5">
            <img src="/logo-lyra-square.png" alt="Lyra" className="size-8" />
          </span>
        </span>
        <span className={`text-[15px] font-[650] text-ink ${labelReveal(expanded)}`}>Lyra</span>
      </div>

      {nav.map((item) => (
        <RailLink key={item.to} {...item} expanded={expanded} />
      ))}

      <div className={`ml-auto flex flex-row items-center gap-1 md:ml-0 md:mt-auto md:flex-col ${expanded ? 'md:w-full md:items-stretch' : ''}`}>
        {/* Collapse/expand toggle — desktop only; lives at the BOTTOM of the
            rail, above the utility icons. Preference persisted to localStorage. */}
        <button
          type="button"
          onClick={toggle}
          aria-label={expanded ? 'Restrânge meniul' : 'Extinde meniul'}
          aria-pressed={expanded}
          className={`hidden shrink-0 rounded-md text-ink-3 hover:bg-wash md:mb-1 md:flex md:h-10 md:items-center md:justify-center ${
            expanded ? 'md:w-full md:justify-start md:pl-1' : 'md:w-10'
          }`}
        >
          <span className="grid size-10 shrink-0 place-items-center">
            {expanded ? <PanelLeftClose className="size-5" /> : <PanelLeftOpen className="size-5" />}
          </span>
          <span className={`text-[13.5px] font-medium ${labelReveal(expanded)}`}>Restrânge</span>
        </button>
        {/* Below md the rail is a single non-scrolling row, so these utilities
            move into the account menu (below) to keep every target on-screen;
            md+ they render inline in the rail as before. */}
        <div className="hidden md:contents">
          <ThemeToggle expanded={expanded} />
          <RailLink to="/ghid" label="Ghid" Icon={BookOpen} expanded={expanded} />
          <RailLink to="/setari" label="Setări" Icon={Settings} expanded={expanded} />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Cont"
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md md:mt-1 ${expanded ? 'md:w-full md:justify-start md:pl-1 md:hover:bg-wash' : ''}`}
            >
              <span className="grid size-10 shrink-0 place-items-center">
                <Avatar className="size-8"><AvatarFallback>{(me?.email ?? '?').slice(0, 1).toUpperCase()}</AvatarFallback></Avatar>
              </span>
              <span className={`min-w-0 text-[13.5px] text-ink-2 ${labelReveal(expanded)}`}>{me?.email ?? 'Cont'}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end">
            <DropdownMenuItem className="md:hidden" onClick={() => navigate('/ghid')}>Ghid</DropdownMenuItem>
            <DropdownMenuItem className="md:hidden" onClick={() => navigate('/setari')}>Setări</DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                signOut()
                  .then(() => { window.location.href = '/login'; })
                  .catch((e) => notifyError(e, 'Deconectarea a eșuat. Reîncercați.'))
              }
            >
              Deconectare
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}
